"use client";

import { useEffect, useRef } from "react";

import { trackEvent } from "@/lib/analytics";

type AnalyticsParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type Options = {
  /** GA4 event name to fire on first qualifying intersection. */
  event: string;
  /** Properties attached to the event. */
  params?: AnalyticsParams;
  /** Visibility threshold (0..1). Defaults to 0.5. */
  threshold?: number;
  /** If true, only fires once per page load (default). */
  once?: boolean;
  /** Skip firing entirely (e.g. flag off). */
  disabled?: boolean;
};

/**
 * Fires an impression event the first time the referenced element crosses
 * the visibility threshold. SSR-safe — no-op on server, no-op if
 * IntersectionObserver is unavailable.
 */
export function useImpression<T extends Element>(
  ref: React.RefObject<T | null>,
  { event, params, threshold = 0.5, once = true, disabled = false }: Options,
) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (disabled) return;
    if (typeof window === "undefined") return;
    if (typeof IntersectionObserver === "undefined") return;
    const el = ref.current;
    if (!el) return;
    if (firedRef.current && once) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            if (once && firedRef.current) return;
            firedRef.current = true;
            trackEvent(event, params);
            if (once) observer.disconnect();
          }
        }
      },
      { threshold: [threshold] },
    );

    observer.observe(el);
    return () => observer.disconnect();
    // We intentionally depend on the stable event name + serialized params so
    // a parent re-render with new params doesn't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, event, JSON.stringify(params), threshold, once, disabled]);
}
